export type FactScope = 'template_owner' | 'organization' | 'member'
export type FieldValue = string | boolean
export type FieldSource =
  | { kind: 'literal'; value: FieldValue }
  | { kind: 'fact'; binding: string; scope?: FactScope }

export interface SigningField {
  name: string
  label: string
  group: string
  type: 'text' | 'checkbox'
  required: boolean
  ownership: 'template' | 'organization'
  source?: FieldSource
  page: number
  rect: [number, number, number, number]
}

export interface TemplatePreview {
  revision: string
  state?: 'missing' | 'pending' | 'ready' | 'failed' | 'needs_attention'
  error_code?: string | null
  pages: { width: number; height: number; url: string }[]
  fields: {
    name: string
    type: string
    page: number
    rect: SigningField['rect']
  }[]
}

export interface SigningSlot {
  label: string
  page: number
  rect: SigningField['rect']
  type: 'signature' | 'date'
}

export interface SigningForm {
  revision?: string
  pages?: TemplatePreview['pages']
  signingSlots?: SigningSlot[]
  key: string
  name: string
  description: string
  fields: SigningField[]
  updatedAt?: string
}

export interface SigningConfiguration {
  id: string
  formKey: string
  name: string
  mappings: Record<string, FieldSource>
  updatedAt?: string
}

export interface SigningFact {
  binding: string
  label: string
  scope: FactScope
  type: SigningField['type']
  field_types?: SigningField['type'][]
}

export interface PreviewMember {
  id: string
  name: string
  values: Record<string, FieldValue>
}

export interface ResolutionContext {
  member?: PreviewMember
  organization: Record<string, FieldValue>
  careforce: Record<string, FieldValue>
}

export const scopeLabels: Record<FactScope, string> = {
  template_owner: 'Careforce',
  organization: 'Organization',
  member: 'Member',
}

export function effectiveSource(
  field: SigningField,
  configuration?: SigningConfiguration
): FieldSource | undefined {
  // A saved org mapping can never win over Careforce ownership.
  return field.ownership === 'template'
    ? field.source
    : configuration?.mappings[field.name]
}

export function resolveSource(
  source: FieldSource | undefined,
  facts: SigningFact[],
  context: ResolutionContext
): FieldValue | undefined {
  if (!source) return undefined
  if (source.kind === 'literal') return source.value
  const fact = facts.find((item) => item.binding === source.binding)
  if (!fact) return undefined
  const scope = source.scope ?? fact.scope
  const values =
    scope === 'member'
      ? context.member?.values
      : scope === 'template_owner'
        ? context.careforce
        : context.organization
  return values?.[source.binding]
}

export function fieldIssue(
  field: SigningField,
  source: FieldSource | undefined,
  facts: SigningFact[]
): string | undefined {
  if (!source)
    return field.required ? 'Choose how this field is filled.' : undefined
  if (source.kind === 'fact') {
    const fact = facts.find((item) => item.binding === source.binding)
    if (!fact) return 'This fact is no longer available. Choose another fact.'
    if (!(fact.field_types ?? [fact.type]).includes(field.type))
      return 'Choose a fact with the right value type.'
    if (
      source.scope &&
      (source.scope === 'member') !== (fact.scope === 'member')
    ) {
      return 'Choose the matching context for this fact.'
    }
    return undefined
  }
  if (
    typeof source.value !== (field.type === 'checkbox' ? 'boolean' : 'string')
  ) {
    return field.type === 'checkbox'
      ? 'Choose checked or unchecked.'
      : 'Enter a text value.'
  }
  if (
    field.required &&
    typeof source.value === 'string' &&
    !source.value.trim()
  ) {
    return 'Enter a value for this required field.'
  }
  return undefined
}

export function configurationIssues(
  form: SigningForm,
  configuration: SigningConfiguration,
  facts: SigningFact[],
  skipManaged = false
): Record<string, string> {
  const issues: Record<string, string> = {}
  for (const field of form.fields) {
    if (skipManaged && field.ownership === 'template') continue
    const issue = fieldIssue(
      field,
      effectiveSource(field, configuration),
      facts
    )
    if (issue) issues[field.name] = issue
  }
  if (form.key === 'mc382') {
    const choices = ['no', 'yes_all_notices_and_mail', 'yes_2'].map((name) => {
      const field = form.fields.find((item) => item.name === name)
      return field ? effectiveSource(field, configuration) : undefined
    })
    if (choices.every((source) => source?.kind === 'literal')) {
      if (
        choices.filter(
          (source) => source?.kind === 'literal' && source.value === true
        ).length !== 1
      ) {
        issues.yes_all_notices_and_mail =
          'Select exactly one notice preference.'
      }
      const limitsField = form.fields.find(
        (field) => field.name === 'notice_limits'
      )
      const limits = limitsField
        ? effectiveSource(limitsField, configuration)
        : undefined
      if (
        choices[2]?.kind === 'literal' &&
        choices[2].value === true &&
        (!limits || (limits.kind === 'literal' && !String(limits.value).trim()))
      ) {
        issues.notice_limits =
          'Specify which notices the representative should receive.'
      }
    }
  }
  return issues
}

export function organizationMappings(
  form: SigningForm,
  mappings: SigningConfiguration['mappings']
): SigningConfiguration['mappings'] {
  return Object.fromEntries(
    form.fields
      .filter(
        (field) =>
          field.ownership === 'organization' &&
          mappings[field.name] !== undefined
      )
      .map((field) => [field.name, mappings[field.name]!])
  )
}
